import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  arrayRemove,
  deleteField,
  Timestamp,
  FieldValue,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import {
  DayPost,NegativeInfluencesLog, 
  DayPostReferences,
  TrainingLog,
  RecoveryLog,
  NiggleLog,
  PerformanceLog,
  HealthLog,
  StepLog,
  NoteLog,
  FemaleCycleLog,
  WellnessLog,
  ActivityLog,
  Session,
  Comment,
  CommentWithUser,
} from "../types/post";
import { getUserPublicData } from "./userService";
import { generateInsights } from "./aiInsightsFunctions";
import { getLinkedSessionIDs } from "../utils/sessionLinks";

// Utility function to generate dateID in YYYYMMDD format
export const generateDateID = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
};

// Utility function to get the last 7 days as dateIDs
export const getLast7Days = (): string[] => {
  const days: string[] = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push(generateDateID(date));
  }

  return days;
};

// Utility function to get the last 28 days as dateIDs
export const getLast28Days = (): string[] => {
  const days: string[] = [];
  const today = new Date();

  for (let i = 0; i < 28; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push(generateDateID(date));
  }

  return days;
};

// Utility function to get more days starting from a given offset
export const getMoreDays = (
  daysToGet: number = 7,
  startOffset: number = 7,
): string[] => {
  const days: string[] = [];
  const today = new Date();

  for (let i = startOffset; i < startOffset + daysToGet; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push(generateDateID(date));
  }

  return days;
};

// Day Post Operations
export const getDayPostReferences = async (
  userID: string,
  dateID: string,
): Promise<DayPostReferences | null> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!dateID || typeof dateID !== "string") {
      throw new Error(`Invalid dateID: ${dateID}`);
    }

    const docRef = doc(db, "dayPosts", userID, "posts", dateID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as DayPostReferences;
    }
    return null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to day posts
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for day post references - user does not have access",
      );
      return null;
    }
    console.error("Error getting day post references:", error);
    throw error;
  }
};

export const createOrUpdateDayPostReferences = async (
  userID: string,
  dateID: string,
  references: Partial<DayPostReferences>,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!dateID || typeof dateID !== "string") {
      throw new Error(`Invalid dateID: ${dateID}`);
    }

    const docRef = doc(db, "dayPosts", userID, "posts", dateID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      await updateDoc(docRef, references);
    } else {
      const defaultReferences: DayPostReferences = {
        negativeInfluenceLogIDs: [],
        trainingLogIDs: [],
        recoveryLogIDs: [],
        niggleLogIDs: [],
        performanceLogIDs: [],
        healthLogIDs: [],
        stepLogIDs: [],
        noteLogIDs: [],
        wellnessLogIDs: [],
        activityLogIDs: [],
        femaleCycleLogIDs: [],
        wearableDayData: [],
        sessionIDs: [],
        likes: [],
        commentIDs: [],
      };
      await setDoc(docRef, { ...defaultReferences, ...references });
    }
  } catch (error) {
    console.error("Error creating/updating day post references:", error);
    throw error;
  }
};

// Generic function to add a log ID to a day post
export const addLogIDToDayPost = async (
  userID: string,
  dateID: string,
  logID: string,
  logType: keyof Pick<
    DayPostReferences,
    | "trainingLogIDs"
    | "recoveryLogIDs"
    | "niggleLogIDs"
    | "performanceLogIDs"
    | "healthLogIDs"
    | "stepLogIDs"
    | "noteLogIDs"
    | "femaleCycleLogIDs"
    | "wellnessLogIDs"
    | "sessionIDs"
    | "negativeInfluenceLogIDs" 
  >,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!dateID || typeof dateID !== "string") {
      throw new Error(`Invalid dateID: ${dateID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "dayPosts", userID, "posts", dateID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const currentData = docSnap.data() as DayPostReferences;
      const currentIDs = currentData[logType] || [];
      if (!currentIDs.includes(logID)) {
        currentIDs.push(logID);
        await updateDoc(docRef, { [logType]: currentIDs });
      }
    } else {
      const defaultReferences: DayPostReferences = {
        negativeInfluenceLogIDs:[],
        trainingLogIDs: [],
        recoveryLogIDs: [],
        niggleLogIDs: [],
        performanceLogIDs: [],
        healthLogIDs: [],
        stepLogIDs: [],
        noteLogIDs: [],
        wellnessLogIDs: [],
        activityLogIDs: [],
        femaleCycleLogIDs: [],
        wearableDayData: [],
        sessionIDs: [],
        likes: [],
        commentIDs: [],
      };
      defaultReferences[logType].push(logID);
      await setDoc(docRef, defaultReferences);
    }
  } catch (error) {
    console.error("Error adding log ID to day post:", error);
    throw error;
  }
};

// Training Log Operations
export const addTrainingLog = async (
  userID: string,
  trainingLog: Omit<TrainingLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...trainingLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "trainingLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error: any) {
    // Permission errors should be thrown for add operations so UI can handle them appropriately
    console.error("Error adding training log:", error);
    throw error;
  }
};

export const getTrainingLog = async (
  userID: string,
  logID: string,
): Promise<TrainingLog | null> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "trainingLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as TrainingLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for training logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting training log:", error);
    throw error;
  }
};

export const getTrainingLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(TrainingLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getTrainingLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is TrainingLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for training logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting training logs:", error);
    throw error;
  }
};
export const addNegativeInfluenceLog = async (
  userID: string,
  log: Omit<NegativeInfluencesLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...log, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "negativeInfluencesLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding negative influence log:", error);
    throw error;
  }
};

export const getNegativeInfluenceLog = async (
  userID: string,
  logID: string,
): Promise<NegativeInfluencesLog | null> => {
  try {
    const docRef = doc(db, "negativeInfluencesLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as NegativeInfluencesLog) : null;
  } catch (error: any) {
    if (error?.code === "permission-denied") return null;
    console.error("Error getting negative influence log:", error);
    throw error;
  }
};

export const getNegativeInfluenceLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(NegativeInfluencesLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getNegativeInfluenceLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is NegativeInfluencesLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting negative influence logs:", error);
    throw error;
  }
};

export const deleteNegativeInfluenceLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "negativeInfluencesLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting negative influence log:", error); 
    throw error;
  }
};

export const updateNegativeInfluenceLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<NegativeInfluencesLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "negativeInfluencesLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating negative influence log:", error);
    throw error;
  }
};
// Recovery Log Operations
export const addRecoveryLog = async (
  userID: string,
  recoveryLog: Omit<RecoveryLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...recoveryLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "recoveryLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding recovery log:", error);
    throw error;
  }
};

export const getRecoveryLog = async (
  userID: string,
  logID: string,
): Promise<RecoveryLog | null> => {
  try {
    const docRef = doc(db, "recoveryLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as RecoveryLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for recovery logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting recovery log:", error);
    throw error;
  }
};

export const getRecoveryLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(RecoveryLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getRecoveryLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is RecoveryLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for recovery logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting recovery logs:", error);
    throw error;
  }
};

// Similar functions for other log types...
// I'll add them in a similar pattern for brevity

// Niggle Log Operations
export const addNiggleLog = async (
  userID: string,
  niggleLog: Omit<NiggleLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...niggleLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "niggleLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding niggle log:", error);
    throw error;
  }
};

export const getNiggleLog = async (
  userID: string,
  logID: string,
): Promise<NiggleLog | null> => {
  try {
    const docRef = doc(db, "niggleLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as NiggleLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for niggle logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting niggle log:", error);
    throw error;
  }
};

export const getNiggleLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(NiggleLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getNiggleLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is NiggleLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for niggle logs - returning empty array");
      return [];
    }
    console.error("Error getting niggle logs:", error);
    throw error;
  }
};

// Performance Log Operations
export const addPerformanceLog = async (
  userID: string,
  performanceLog: Omit<PerformanceLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...performanceLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "performanceLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding performance log:", error);
    throw error;
  }
};

export const getPerformanceLog = async (
  userID: string,
  logID: string,
): Promise<PerformanceLog | null> => {
  try {
    const docRef = doc(db, "performanceLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as PerformanceLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for performance logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting performance log:", error);
    throw error;
  }
};

/**
 * Results (performance logs) whose `linkedTrainingLogID` points at the given
 * training log — the rows of a Performance (PerformanceLog.md §4).
 */
export const getResultsForTrainingLog = async (
  userID: string,
  trainingLogID: string,
): Promise<(PerformanceLog & { id: string })[]> => {
  if (!userID || !trainingLogID) return [];
  try {
    const q = query(
      collection(db, "performanceLogs", userID, "logs"),
      where("linkedTrainingLogID", "==", trainingLogID),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ ...(d.data() as PerformanceLog), id: d.id }))
      .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
  } catch (error: any) {
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for performance logs - returning []");
      return [];
    }
    console.error("Error getting results for training log:", error);
    throw error;
  }
};

/**
 * Every performance training log of the user (those with `performanceType`),
 * newest first. Powers the Performances screen and Goals history.
 */
export const getAllPerformanceTrainingLogs = async (
  userID: string,
): Promise<(TrainingLog & { id: string })[]> => {
  if (!userID) return [];
  try {
    const q = query(
      collection(db, "trainingLogs", userID, "logs"),
      where("performanceType", "in", ["competition", "gameDay", "testing"]),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ ...(d.data() as TrainingLog), id: d.id }))
      .sort((a, b) => (b.dateID ?? "").localeCompare(a.dateID ?? ""));
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting performance training logs:", error);
    throw error;
  }
};

/**
 * Training logs in a dateID range (inclusive). Used for performance history
 * (filter `performanceType` client-side).
 */
export const getTrainingLogsForDateRange = async (
  userID: string,
  startDateID: string,
  endDateID: string,
): Promise<(TrainingLog & { id: string })[]> => {
  if (!userID) return [];
  try {
    const q = query(
      collection(db, "trainingLogs", userID, "logs"),
      where("dateID", ">=", startDateID),
      where("dateID", "<=", endDateID),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...(d.data() as TrainingLog), id: d.id }));
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting training logs for date range:", error);
    throw error;
  }
};

export const getPerformanceLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(PerformanceLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getPerformanceLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is PerformanceLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for performance logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting performance logs:", error);
    throw error;
  }
};

/**
 * Fetch every performance log for a user. Used by the Performances library
 * page which needs to enumerate logs across all dates for sorting, paging and
 * bulk operations.
 */
export const getAllPerformanceLogs = async (
  userID: string,
): Promise<(PerformanceLog & { id: string })[]> => {
  try {
    const logsRef = collection(db, "performanceLogs", userID, "logs");
    const snapshot = await getDocs(logsRef);
    return snapshot.docs.map((d) => ({
      ...(d.data() as PerformanceLog),
      id: d.id,
    }));
  } catch (error: any) {
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for performance logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting all performance logs:", error);
    throw error;
  }
};

/**
 * Fetch all performance logs for a user whose performanceDate falls within the
 * inclusive [startDateID, endDateID] range (both in YYYYMMDD format). Used by
 * the Activity & Exercise insights chart to plot performances directly, since
 * performances no longer create a session. Legacy logs without a performanceDate
 * are not returned (they are covered by the session-based path).
 */
export const getPerformanceLogsForDateRange = async (
  userID: string,
  startDateID: string,
  endDateID: string,
): Promise<(PerformanceLog & { id: string })[]> => {
  try {
    const logsRef = collection(db, "performanceLogs", userID, "logs");
    const logsQuery = query(
      logsRef,
      where("performanceDate", ">=", startDateID),
      where("performanceDate", "<=", endDateID),
    );
    const snapshot = await getDocs(logsQuery);
    return snapshot.docs.map((d) => ({
      ...(d.data() as PerformanceLog),
      id: d.id,
    }));
  } catch (error: any) {
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for performance logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting performance logs for date range:", error);
    throw error;
  }
};

// Health Log Operations
export const addHealthLog = async (
  userID: string,
  healthLog: Partial<Omit<HealthLog, "timestamp" | "dateID">>,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = Object.fromEntries(
      Object.entries({ ...healthLog, timestamp: Date.now(), dateID }).filter(
        ([, value]) => value !== undefined,
      ),
    );
    const docRef = await addDoc(
      collection(db, "healthLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding health log:", error);
    throw error;
  }
};

export const getHealthLog = async (
  userID: string,
  logID: string,
): Promise<HealthLog | null> => {
  try {
    const docRef = doc(db, "healthLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as HealthLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for health logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting health log:", error);
    throw error;
  }
};

export const getHealthLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(HealthLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getHealthLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is HealthLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for health logs - returning empty array");
      return [];
    }
    console.error("Error getting health logs:", error);
    throw error;
  }
};

// Step Log Operations
export const addStepLog = async (
  userID: string,
  stepLog: Omit<StepLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...stepLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "stepLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding step log:", error);
    throw error;
  }
};

export const getStepLog = async (
  userID: string,
  logID: string,
): Promise<StepLog | null> => {
  try {
    const docRef = doc(db, "stepLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as StepLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for step logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting step log:", error);
    throw error;
  }
};

export const getStepLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(StepLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getStepLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter((log): log is StepLog & { id: string } => log !== null);
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for step logs - returning empty array");
      return [];
    }
    console.error("Error getting step logs:", error);
    throw error;
  }
};

// Note Log Operations
export const addNoteLog = async (
  userID: string,
  noteLog: Omit<NoteLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...noteLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "noteLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding note log:", error);
    throw error;
  }
};

export const getNoteLog = async (
  userID: string,
  logID: string,
): Promise<NoteLog | null> => {
  try {
    const docRef = doc(db, "noteLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as NoteLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for note logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting note log:", error);
    throw error;
  }
};

export const getNoteLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(NoteLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getNoteLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter((log): log is NoteLog & { id: string } => log !== null);
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for note logs - returning empty array");
      return [];
    }
    console.error("Error getting note logs:", error);
    throw error;
  }
};

// Female Cycle Log Operations
export const getFemaleCycleLog = async (
  userID: string,
  logID: string,
): Promise<FemaleCycleLog | null> => {
  try {
    const docRef = doc(db, "femaleCycleLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as FemaleCycleLog) : null;
  } catch (error: any) {
    // Gracefully handle permission errors
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for female cycle log");
      return null;
    }
    console.error("Error getting female cycle log:", error);
    throw error;
  }
};

export const getFemaleCycleLogs = async (
  userID: string,
  logIDs: string[] = [],
): Promise<(FemaleCycleLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getFemaleCycleLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is FemaleCycleLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for female cycle logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting female cycle logs:", error);
    throw error;
  }
};

// Wellness Log Operations
export const addWellnessLog = async (
  userID: string,
  wellnessLog: Omit<WellnessLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    const logWithTimestamp = { ...wellnessLog, timestamp: Date.now(), dateID };
    const docRef = await addDoc(
      collection(db, "wellnessLogs", userID, "logs"),
      logWithTimestamp,
    );
    return docRef.id;
  } catch (error) {
    console.error("Error adding wellness log:", error);
    throw error;
  }
};

export const getWellnessLog = async (
  userID: string,
  logID: string,
): Promise<WellnessLog | null> => {
  try {
    const docRef = doc(db, "wellnessLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as WellnessLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for wellness logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting wellness log:", error);
    throw error;
  }
};

// Delete operations for logs

/**
 * Delete a training log and resolve its linked sessions / wearable activity
 * (plan decision 5, "Delete the linked sessions and activity data too?").
 *
 * - `deleteLinkedSessionsAndActivity = true`: deletes every session in
 *   `linkedSessionIDs` and the `linkedActivityLogID` activity log, removing
 *   each id from its day post's `sessionIDs` / `activityLogIDs`.
 * - `false`: clears `linkedTrainingLogID` on each linked session (they stay
 *   `complete` and reappear under "Sessions to log") and on the activity log.
 *
 * Everything is committed in one batch. The caller is still responsible for
 * removing the log id from `dayPosts.trainingLogIDs` (as before).
 *
 * Performance training logs (PerformanceLog.md §4 "Delete"): when
 * `options.deleteLinkedResults` is true the result docs pointing at this log
 * are deleted and removed from their day posts; otherwise their
 * `linkedTrainingLogID` is cleared so they survive as legacy result-only
 * logs. Event "Logged" state is derived, so nothing else needs resetting.
 */
export const deleteTrainingLog = async (
  userID: string,
  logID: string,
  deleteLinkedSessionsAndActivity: boolean,
  options?: { deleteLinkedResults?: boolean },
): Promise<void> => {
  try {
    const logRef = doc(db, "trainingLogs", userID, "logs", logID);
    const logSnap = await getDoc(logRef);
    const log = logSnap.exists() ? (logSnap.data() as TrainingLog) : null;

    const batch = writeBatch(db);
    const dayPostsToPatch = new Map<
      string,
      { sessionIDs: string[]; activityLogIDs: string[]; performanceLogIDs: string[] }
    >();
    const queueDayPostRemoval = (
      dateID: string | undefined,
      field: "sessionIDs" | "activityLogIDs" | "performanceLogIDs",
      id: string,
    ) => {
      if (!dateID) return;
      const entry = dayPostsToPatch.get(dateID) ?? {
        sessionIDs: [],
        activityLogIDs: [],
        performanceLogIDs: [],
      };
      entry[field].push(id);
      dayPostsToPatch.set(dateID, entry);
    };

    // Linked results of a performance training log.
    if (log?.performanceType) {
      const results = await getResultsForTrainingLog(userID, logID);
      results.forEach((result) => {
        const resultRef = doc(db, "performanceLogs", userID, "logs", result.id);
        if (options?.deleteLinkedResults) {
          queueDayPostRemoval(result.dateID, "performanceLogIDs", result.id);
          batch.delete(resultRef);
        } else {
          batch.update(resultRef, { linkedTrainingLogID: deleteField() });
        }
      });
    }

    const sessionIDs = getLinkedSessionIDs(log);
    const sessionRefs = sessionIDs.map((id) =>
      doc(db, "sessions", userID, "sessions", id),
    );
    const sessionSnaps = await Promise.all(sessionRefs.map((ref) => getDoc(ref)));
    sessionSnaps.forEach((snap, index) => {
      if (!snap.exists()) return;
      const sessionRef = sessionRefs[index];
      if (deleteLinkedSessionsAndActivity) {
        const session = snap.data() as Session;
        queueDayPostRemoval(
          session.plannedDate?.replace(/-/g, ""),
          "sessionIDs",
          sessionIDs[index],
        );
        batch.delete(sessionRef);
      } else {
        batch.update(sessionRef, {
          linkedTrainingLogID: deleteField(),
          updatedAt: Timestamp.now(),
        });
      }
    });

    if (log?.linkedActivityLogID) {
      const activityRef = doc(
        db,
        "activityLogs",
        userID,
        "logs",
        log.linkedActivityLogID,
      );
      const activitySnap = await getDoc(activityRef);
      if (activitySnap.exists()) {
        if (deleteLinkedSessionsAndActivity) {
          const activity = activitySnap.data() as ActivityLog;
          queueDayPostRemoval(
            activity.dateID,
            "activityLogIDs",
            log.linkedActivityLogID,
          );
          batch.delete(activityRef);
        } else {
          batch.update(activityRef, { linkedTrainingLogID: deleteField() });
        }
      }
    }

    for (const [dateID, ids] of Array.from(dayPostsToPatch.entries())) {
      const dayPostRef = doc(db, "dayPosts", userID, "posts", dateID);
      const dayPostSnap = await getDoc(dayPostRef);
      if (!dayPostSnap.exists()) continue;
      const patch: Record<string, FieldValue> = {};
      if (ids.sessionIDs.length > 0) {
        patch.sessionIDs = arrayRemove(...ids.sessionIDs);
      }
      if (ids.activityLogIDs.length > 0) {
        patch.activityLogIDs = arrayRemove(...ids.activityLogIDs);
      }
      if (ids.performanceLogIDs.length > 0) {
        patch.performanceLogIDs = arrayRemove(...ids.performanceLogIDs);
      }
      batch.update(dayPostRef, patch);
    }

    batch.delete(logRef);
    await batch.commit();
  } catch (error) {
    console.error("Error deleting training log:", error);
    throw error;
  }
};

export const deleteRecoveryLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "recoveryLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting recovery log:", error);
    throw error;
  }
};

export const deleteNiggleLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "niggleLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting niggle log:", error);
    throw error;
  }
};

export const deletePerformanceLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "performanceLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting performance log:", error);
    throw error;
  }
};

export const deleteHealthLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "healthLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting health log:", error);
    throw error;
  }
};

export const deleteStepLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "stepLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting step log:", error);
    throw error;
  }
};

export const deleteNoteLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "noteLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting note log:", error);
    throw error;
  }
};

export const deleteWellnessLog = async (
  userID: string,
  logID: string,
): Promise<void> => {
  try {
    const docRef = doc(db, "wellnessLogs", userID, "logs", logID);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting wellness log:", error);
    throw error;
  }
};

// Function to remove a log ID from a day post
export const removeLogIDFromDayPost = async (
  userID: string,
  dateID: string,
  logID: string,
  logType: keyof Pick<
    DayPostReferences,
    | "trainingLogIDs"
    | "recoveryLogIDs"
    | "niggleLogIDs"
    | "performanceLogIDs"
    | "healthLogIDs"
    | "stepLogIDs"
    | "noteLogIDs"
    | "femaleCycleLogIDs"
    | "wellnessLogIDs"
    | "sessionIDs"
    | "negativeInfluenceLogIDs"
  >,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!dateID || typeof dateID !== "string") {
      throw new Error(`Invalid dateID: ${dateID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "dayPosts", userID, "posts", dateID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const currentData = docSnap.data() as DayPostReferences;
      const currentIDs = currentData[logType] || [];
      const updatedIDs = currentIDs.filter((id) => id !== logID);
      await updateDoc(docRef, { [logType]: updatedIDs });
    }
  } catch (error) {
    console.error("Error removing log ID from day post:", error);
    throw error;
  }
};

// Update operations for logs
export const updateTrainingLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<TrainingLog, "timestamp">>,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "trainingLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating training log:", error);
    throw error;
  }
};

export const updateRecoveryLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<RecoveryLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "recoveryLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating recovery log:", error);
    throw error;
  }
};

export const updateNiggleLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<NiggleLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "niggleLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating niggle log:", error);
    throw error;
  }
};

export const updatePerformanceLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<PerformanceLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "performanceLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating performance log:", error);
    throw error;
  }
};

export const updateHealthLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<HealthLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "healthLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating health log:", error);
    throw error;
  }
};

export const updateActivityLog = async (
  userID: string,
  logID: string,
  updates: Partial<
    Omit<ActivityLog, "terraActivityDataID" | "startTime" | "endTime">
  >,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "activityLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating activity log:", error);
    throw error;
  }
};

// Activity-Training Linking Functions
export const linkActivityToTraining = async (
  userID: string,
  activityLogID: string,
  trainingLogID: string,
): Promise<void> => {
  try {
    // Update both logs simultaneously
    await Promise.all([
      updateActivityLog(userID, activityLogID, {
        linkedTrainingLogID: trainingLogID,
      }),
      updateTrainingLog(userID, trainingLogID, {
        linkedActivityLogID: activityLogID,
      }),
    ]);
  } catch (error) {
    console.error("Error linking activity to training:", error);
    throw error;
  }
};

export const unlinkActivityFromTraining = async (
  userID: string,
  activityLogID: string,
  trainingLogID: string,
): Promise<void> => {
  try {
    if (!userID || !activityLogID || !trainingLogID) {
      throw new Error("userID, activityLogID and trainingLogID are required");
    }
    // Firestore rejects `undefined` values, so the fields are removed with
    // deleteField() in one batch instead of two updates.
    const batch = writeBatch(db);
    batch.update(doc(db, "activityLogs", userID, "logs", activityLogID), {
      linkedTrainingLogID: deleteField(),
    });
    batch.update(doc(db, "trainingLogs", userID, "logs", trainingLogID), {
      linkedActivityLogID: deleteField(),
    });
    await batch.commit();
  } catch (error) {
    console.error("Error unlinking activity from training:", error);
    throw error;
  }
};

export const createTrainingLogWithActivityLink = async (
  userID: string,
  activityLogID: string,
  trainingLog: Omit<TrainingLog, "timestamp" | "linkedActivityLogID" | "dateID">,
  dateID: string,
): Promise<string> => {
  try {
    // Create training log with activity link
    const trainingLogWithLink = {
      ...trainingLog,
      linkedActivityLogID: activityLogID,
    };

    const trainingLogId = await addTrainingLog(userID, trainingLogWithLink, dateID);

    // Update activity log with training link
    await updateActivityLog(userID, activityLogID, {
      linkedTrainingLogID: trainingLogId,
    });

    return trainingLogId;
  } catch (error) {
    console.error("Error creating training log with activity link:", error);
    throw error;
  }
};

export const updateStepLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<StepLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "stepLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating step log:", error);
    throw error;
  }
};

export const updateNoteLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<NoteLog, "timestamp">>,
): Promise<void> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "noteLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating note log:", error);
    throw error;
  }
};

export const updateWellnessLog = async (
  userID: string,
  logID: string,
  updates: Partial<Omit<WellnessLog, "timestamp">>,
): Promise<void> => {
  try {
    const docRef = doc(db, "wellnessLogs", userID, "logs", logID);
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error("Error updating wellness log:", error);
    throw error;
  }
};

export const getWellnessLogs = async (
  userID: string,
  logIDs: string[],
): Promise<(WellnessLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getWellnessLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is WellnessLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for wellness logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting wellness logs:", error);
    throw error;
  }
};

// Peak Score Log Operations
export const getPeakScoreLog = async (
  userID: string,
  logID: string,
): Promise<any | null> => {
  try {
    const docRef = doc(db, "peakScoreLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    console.log("docSnap for peak score log", docSnap.data());
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for peak score logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting peak score log:", error);
    throw error;
  }
};

// Activity Log Operations
export const getActivityLog = async (
  userID: string,
  logID: string,
): Promise<ActivityLog | null> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!logID || typeof logID !== "string") {
      throw new Error(`Invalid logID: ${logID}`);
    }

    const docRef = doc(db, "activityLogs", userID, "logs", logID);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as ActivityLog) : null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to this log type
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for activity logs - user does not have access",
      );
      return null;
    }
    console.error("Error getting activity log:", error);
    throw error;
  }
};

export const getActivityLogs = async (
  userID: string,
  logIDs: string[],
): Promise<(ActivityLog & { id: string })[]> => {
  try {
    const logs = await Promise.all(
      logIDs.map(async (logID) => {
        const log = await getActivityLog(userID, logID);
        return log ? { ...log, id: logID } : null;
      }),
    );
    return logs.filter(
      (log): log is ActivityLog & { id: string } => log !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for activity logs - returning empty array",
      );
      return [];
    }
    console.error("Error getting activity logs:", error);
    throw error;
  }
};

// Session Operations
export const getSession = async (
  userID: string,
  sessionID: string,
): Promise<Session | null> => {
  try {
    // Validate parameters
    if (!userID || typeof userID !== "string") {
      throw new Error(`Invalid userID: ${userID}`);
    }
    if (!sessionID || typeof sessionID !== "string") {
      throw new Error(`Invalid sessionID: ${sessionID}`);
    }

    const docRef = doc(db, "sessions", userID, "sessions", sessionID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Session;
    }
    return null;
  } catch (error: any) {
    // Handle permission errors gracefully - user doesn't have access to sessions
    if (error?.code === "permission-denied") {
      console.warn(
        "Permission denied for sessions - user does not have access",
      );
      return null;
    }
    console.error("Error getting session:", error);
    throw error;
  }
};

export const getSessions = async (
  userID: string,
  sessionIDs: string[] = [],
): Promise<(Session & { id: string })[]> => {
  try {
    const sessions = await Promise.all(
      sessionIDs.map(async (sessionID) => {
        const session = await getSession(userID, sessionID);
        return session ? { ...session, id: sessionID } : null;
      }),
    );
    return sessions.filter(
      (session): session is Session & { id: string } => session !== null,
    );
  } catch (error: any) {
    // Handle permission errors gracefully - return empty array if user has no access
    if (error?.code === "permission-denied") {
      console.warn("Permission denied for sessions - returning empty array");
      return [];
    }
    console.error("Error getting sessions:", error);
    throw error;
  }
};

// Main function to get a complete DayPost
export const getDayPost = async (
  userID: string,
  dateID: string,
): Promise<DayPost | null> => {
  try {
    const references = await getDayPostReferences(userID, dateID);

    if (!references) {
      return null;
    }

    // Fetch all logs in parallel - each function handles permission errors gracefully
    const logResults = await Promise.allSettled([
      getNegativeInfluenceLogs(userID, references.negativeInfluenceLogIDs || []),
      getTrainingLogs(userID, references.trainingLogIDs),
      getRecoveryLogs(userID, references.recoveryLogIDs),
      getNiggleLogs(userID, references.niggleLogIDs),
      getPerformanceLogs(userID, references.performanceLogIDs),
      getHealthLogs(userID, references.healthLogIDs),
      getStepLogs(userID, references.stepLogIDs),
      getNoteLogs(userID, references.noteLogIDs),
      getFemaleCycleLogs(userID, references.femaleCycleLogIDs || []),
      getWellnessLogs(userID, references.wellnessLogIDs),
      getActivityLogs(userID, references.activityLogIDs || []),
      getSessions(userID, references.sessionIDs || []),
    ]);

    // Extract results, handling failures gracefully
    const negativeInfluencesLogs =
  logResults[0].status === "fulfilled" ? logResults[0].value : [];
const trainingLogs =
  logResults[1].status === "fulfilled" ? logResults[1].value : [];
const recoveryLogs =
  logResults[2].status === "fulfilled" ? logResults[2].value : [];
const niggleLogs =
  logResults[3].status === "fulfilled" ? logResults[3].value : [];
const performanceLogs =
  logResults[4].status === "fulfilled" ? logResults[4].value : [];
const healthLogs =
  logResults[5].status === "fulfilled" ? logResults[5].value : [];
const stepLogs =
  logResults[6].status === "fulfilled" ? logResults[6].value : [];
const noteLogs =
  logResults[7].status === "fulfilled" ? logResults[7].value : [];
const femaleCycleLogs =
  logResults[8].status === "fulfilled" ? logResults[8].value : [];
const wellnessLogs =
  logResults[9].status === "fulfilled" ? logResults[9].value : [];
const activityLogs =
  logResults[10].status === "fulfilled" ? logResults[10].value : [];
const sessions =
  logResults[11].status === "fulfilled" ? logResults[11].value : [];

    // Fetch peak score log if it exists
    let peakScoreLog: any = undefined;
    if (references.peakScoreLogID) {
      try {
        const peakScoreData = await getPeakScoreLog(
          userID,
          references.peakScoreLogID,
        );
        if (peakScoreData) {
          peakScoreLog = { ...peakScoreData, id: references.peakScoreLogID };
        }
      } catch (error) {
        console.warn("Failed to fetch peak score log:", error);
      }
    }

    // Log any failures for debugging
    logResults.forEach((result, index) => {
      if (result.status === "rejected") {
        const logTypes = [
          "negativeInfluence",
          "training",
          "recovery",
          "niggle",
          "performance",
          "health",
          "step",
          "note",
          "femaleCycle",
          "wellness",
          "activity",
          "sessions",
        ];
        console.warn(`Failed to fetch ${logTypes[index]} logs:`, result.reason);
      }
    });

    const dayPost: DayPost = {
      dateID,
      negativeInfluencesLogs,
      trainingLogs,
      recoveryLogs,
      niggleLogs,
      performanceLogs,
      healthLogs,
      stepLogs,
      noteLogs,
      femaleCycleLogs,
      wellnessLogs,
      activityLogs,
      wearableDayData: references.wearableDayData || [],
      peakScoreLog,
      sessions,
      likes: references.likes,
      comments: [], // Will be implemented later
    };

    return dayPost;
  } catch (error) {
    console.error("Error getting day post:", error);
    throw error;
  }
};

// Get multiple day posts (for dashboard)
export const getDayPosts = async (
  userID: string,
  dateIDs: string[],
): Promise<DayPost[]> => {
  try {
    const dayPosts = await Promise.all(
      dateIDs.map(async (dateID) => {
        const dayPost = await getDayPost(userID, dateID);
        return (
          dayPost ||
          ({
            dateID,
            negativeInfluencesLogs: [], 
            trainingLogs: [],
            recoveryLogs: [],
            niggleLogs: [],
            performanceLogs: [],
            healthLogs: [],
            stepLogs: [],
            noteLogs: [],
            femaleCycleLogs: [],
            wellnessLogs: [],
            activityLogs: [],
            sessions: [],
            likes: [],
            comments: [],
          } as DayPost)
        );
      }),
    );

    return dayPosts;
  } catch (error) {
    console.error("Error getting day posts:", error);
    throw error;
  }
};

// Check which log types a user has read access to
export const checkLogTypePermissions = async (
  userID: string,
): Promise<{
  trainingLogs: boolean;
  recoveryLogs: boolean;
  niggleLogs: boolean;
  performanceLogs: boolean;
  healthLogs: boolean;
  stepLogs: boolean;
  noteLogs: boolean;
  wellnessLogs: boolean;
}> => {
  const permissions = {
    trainingLogs: true,
    recoveryLogs: true,
    niggleLogs: true,
    performanceLogs: true,
    healthLogs: true,
    stepLogs: true,
    noteLogs: true,
    wellnessLogs: true,
  };

  // Test read access to each collection by attempting to get a non-existent document
  const testPromises = [
    getTrainingLog(userID, "test-permissions"),
    getRecoveryLog(userID, "test-permissions"),
    getNiggleLog(userID, "test-permissions"),
    getPerformanceLog(userID, "test-permissions"),
    getHealthLog(userID, "test-permissions"),
    getStepLog(userID, "test-permissions"),
    getNoteLog(userID, "test-permissions"),
    getWellnessLog(userID, "test-permissions"),
  ];

  const results = await Promise.allSettled(testPromises);
  const logTypes = [
    "trainingLogs",
    "recoveryLogs",
    "niggleLogs",
    "performanceLogs",
    "healthLogs",
    "stepLogs",
    "noteLogs",
    "wellnessLogs",
  ] as const;

  results.forEach((result, index) => {
    if (result.status === "rejected") {
      const error = result.reason;
      if (error?.code === "permission-denied") {
        permissions[logTypes[index]] = false;
      }
    }
  });

  return permissions;
};

// Peak Score Calculation Service
export const calculateAndSavePeakScore = async (
  dateID: string,
): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> => {
  try {
    const calculatePeakScore = httpsCallable(
      functions,
      "calculateAndSavePeakScore",
    );
    const result = await calculatePeakScore({
      dateID: dateID,
    });

    console.log("Peak score calculation result:", result.data);
    return result.data as any;
  } catch (error) {
    console.error("Error calling calculateAndSavePeakScore:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// Fast wellness log save: writes the wellness log and links it to the day post,
// without waiting for the peak score calculation. Intended for flows where the
// UI should not block on the (slow) peak score Cloud Function call.
export const addWellnessLogAndLinkToDayPost = async (
  userID: string,
  wellnessLog: Omit<WellnessLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<{ wellnessLogId: string }> => {
  const wellnessLogId = await addWellnessLog(userID, wellnessLog, dateID);

  const dayPostDateID =
    dateID ||
    (() => {
      const today = new Date();
      return (
        today.getFullYear().toString() +
        (today.getMonth() + 1).toString().padStart(2, "0") +
        today.getDate().toString().padStart(2, "0")
      );
    })();

  await addLogIDToDayPost(
    userID,
    dayPostDateID,
    wellnessLogId,
    "wellnessLogIDs",
  );

  return { wellnessLogId };
};

// Enhanced Wellness Log Operations with Peak Score Calculation
export const addWellnessLogWithPeakScore = async (
  userID: string,
  wellnessLog: Omit<WellnessLog, "timestamp" | "dateID">,
  dateID: string,
): Promise<{ wellnessLogId: string; peakScoreResult?: any }> => {
  try {
    // Save the wellness log and link it to the day post first so the peak
    // score calculation can see the wellness data.
    const { wellnessLogId } = await addWellnessLogAndLinkToDayPost(
      userID,
      wellnessLog,
      dateID,
    );
    console.log(
      "Added wellness log ID to day post before peak score calculation",
    );

    // Now calculate and save the peak score (it will see the wellness log)
    const peakScoreResult = await calculateAndSavePeakScore(dateID);

    if (!peakScoreResult.success) {
      console.warn(
        "Peak score calculation failed, but wellness log was saved:",
        peakScoreResult.error,
      );
    } else {
      console.log(
        "Peak score calculated and saved successfully:",
        peakScoreResult.data,
      );
    }

    return {
      wellnessLogId,
      peakScoreResult: peakScoreResult.success
        ? peakScoreResult.data
        : undefined,
    };
  } catch (error) {
    console.error("Error adding wellness log with peak score:", error);
    throw error;
  }
};

// Comment Operations
export const addCommentToDayPost = async (
  athleteId: string,
  dayPostId: string,
  commentText: string,
  coachId: string,
): Promise<Comment | null> => {
  try {
    console.log(
      `\n💬 Adding comment to day post ${dayPostId} for athlete ${athleteId}`,
    );

    console.log(
      `Comment Reference Path: dayPosts/${athleteId}/posts/${dayPostId}/comments`,
    );

    const commentsRef = collection(
      db,
      "dayPosts",
      athleteId,
      "posts",
      dayPostId,
      "comments",
    );

    const newComment: Omit<Comment, "timestamp"> & { timestamp: any } = {
      comment: commentText,
      userID: coachId,
      timestamp: serverTimestamp(),
    };

    const docRef = await addDoc(commentsRef, newComment);
    console.log(`✅ Comment added with ID: ${docRef.id}`);

    // Return the comment with the current timestamp (approximation)
    const addedComment: Comment = {
      comment: commentText,
      userID: coachId,
      timestamp: Date.now(),
    };

    return addedComment;
  } catch (error) {
    console.error(`Error adding comment to day post ${dayPostId}:`, error);
    return null;
  }
};

export const getDayPostComments = async (
  athleteId: string,
  dayPostId: string,
): Promise<CommentWithUser[]> => {
  try {
    // console.log(
    //   `\n💬 Fetching comments for day post ${dayPostId} of athlete ${athleteId}`,
    // );

    const commentsRef = collection(
      db,
      "dayPosts",
      athleteId,
      "posts",
      dayPostId,
      "comments",
    );
    const q = query(commentsRef, orderBy("timestamp", "desc"));

    const querySnapshot = await getDocs(q);

    // Fetch comments and their user data in parallel
    const commentsWithUserPromises = querySnapshot.docs.map(
      async (commentDoc) => {
        const data = commentDoc.data();
        const baseComment: Comment = {
          comment: data.comment,
          userID: data.userID,
          timestamp: data.timestamp?.seconds
            ? data.timestamp.seconds * 1000
            : data.timestamp,
        };

        // Fetch user data. Uses the public `users/{uid}` doc only — the
        // commenter and the day-post owner aren't necessarily the same
        // person (e.g. a coach commenting on an athlete's day), and
        // `users/{uid}/private/data` is gated by `canAccessUserThroughOrg`,
        // which isn't guaranteed to hold in this direction. `username` is
        // public precisely so it's safe to show here regardless. Matches
        // PeakElite's `athleteService.getDayPostComments`.
        try {
          const publicData = await getUserPublicData(data.userID);

          const commentWithUser: CommentWithUser = {
            ...baseComment,
            id: commentDoc.id,
            user: {
              name: publicData?.username,
              imageUrl: publicData?.imageUrl,
              imageUrlTiny: publicData?.imageUrlTiny,
            },
          };

          return commentWithUser;
        } catch (error) {
          console.error(
            `Error fetching user data for user ${data.userID}:`,
            error,
          );
          // Return comment without user data if fetch fails
          return { ...baseComment, id: commentDoc.id, user: undefined };
        }
      },
    );

    const comments = await Promise.all(commentsWithUserPromises);

    // console.log(`✅ Found ${comments.length} comments with user data`);
    return comments;
  } catch (error) {
    // console.error(`Error fetching comments for day post ${dayPostId}:`, error);
    return [];
  }
};

export const editCommentOnDayPost = async (
  athleteId: string,
  dayPostId: string,
  commentId: string,
  newText: string,
): Promise<void> => {
  try {
    const commentRef = doc(
      db,
      "dayPosts",
      athleteId,
      "posts",
      dayPostId,
      "comments",
      commentId,
    );
    await updateDoc(commentRef, {
      comment: newText,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error editing day post comment:", error);
    throw error;
  }
};

export const deleteCommentOnDayPost = async (
  athleteId: string,
  dayPostId: string,
  commentId: string,
): Promise<void> => {
  try {
    const commentRef = doc(
      db,
      "dayPosts",
      athleteId,
      "posts",
      dayPostId,
      "comments",
      commentId,
    );
    await deleteDoc(commentRef);
  } catch (error) {
    console.error("Error deleting day post comment:", error);
    throw error;
  }
};
